/**
 * useVirtualAccountData Hook
 * 가상계좌 데이터 관리 훅
 */
import {useCallback, useState} from 'react';
import {virtualAccountApi} from '../api/virtualAccountApi';
import {VirtualAccount} from '../types/virtualAccount.types';
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';

export const useVirtualAccountData = () => {
    const [accounts, setAccounts] = useState<VirtualAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const { showToast } = useToast();

    // ======== 전체 목록 조회 ========

    const fetchAllAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await virtualAccountApi.getVirtualAccounts(0, 9999);
            if (Array.isArray(response)) {
                setAccounts(response);
            } else if (response.content) {
                setAccounts(response.content);
            } else {
                setAccounts([]);
            }
        } catch {
            showToast('가상계좌 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // ======== 협력사 ID 검색 ========

    const searchBySuppId = useCallback(async (suppId: string) => {
        setLoading(true);
        try {
            const result = await virtualAccountApi.getVirtualAccountBySuppId(suppId);
            setAccounts(Array.isArray(result) ? result : [result]);
            setIsSearchMode(true);
        } catch (error) {
            const err = error as { originalError?: { response?: { data?: { message?: string } } }; message?: string };
            const errorMsg = err?.originalError?.response?.data?.message || err?.message || '검색에 실패했습니다.';
            showToast(errorMsg, { type: ToastType.ERROR });
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // ======== 라이더 ID 검색 ========

    const searchByRiderId = useCallback(async (riderId: string) => {
        setLoading(true);
        try {
            const result = await virtualAccountApi.getVirtualAccountByRiderId(riderId);
            setAccounts(Array.isArray(result) ? result : [result]);
            setIsSearchMode(true);
        } catch (error) {
            const err = error as { originalError?: { response?: { data?: { message?: string } } }; message?: string };
            const errorMsg = err?.originalError?.response?.data?.message || err?.message || '검색에 실패했습니다.';
            showToast(errorMsg, { type: ToastType.ERROR });
            setAccounts([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // ======== 검색 초기화 ========

    const resetSearch = useCallback(async () => {
        setIsSearchMode(false);
        await fetchAllAccounts();
    }, [fetchAllAccounts]);

    return {
        accounts,
        loading,
        isSearchMode,
        fetchAllAccounts,
        searchBySuppId,
        searchByRiderId,
        resetSearch,
    };
};
